import { useBrandProducts, useShowSpinner } from 'hooks';
import { useEffect, useState } from 'react';
import { BRAND_PRODUCTS_PAGE_SIZE_DEFAULT } from 'types/brandProducts';
import BrandProductsView from './BrandProducts.View';
import getConfig from './brandProductsTableConfig';
import BrandProductsTaxonomyChangeModal from './Layout/BrandProductsTaxonomyChangeModal';

const BrandProducts = () => {
  const {
    tableRef,
    products,
    productsPagination,
    onNextProductsBatch,
    onPreviousProductsBatch,
    onNextTaxonomiesBatch,
    onTaxonomyFilterChange,
    upsertProductTaxonomies,
    selectedProducts,
    taxonomies,
    taxonomyPagination,
    productLoading,
    taxonomyLoading,
    onProductRowSelect,
    clearSelectedProducts
  } = useBrandProducts();

  const [show, setShow] = useState(false);
  const [view, setView] = useState<'table' | 'cards'>('table');

  useShowSpinner({ show: productLoading });

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    /* fetch initial batch on load */
    onNextProductsBatch();
  }, []);

  // Number of items displayed up to this page
  const displayed = Math.min(
    productsPagination.page * BRAND_PRODUCTS_PAGE_SIZE_DEFAULT,
    productsPagination.totalItems
  );

  const onOpenClassificationModal = () => setShow(true);

  const onCloseClassificationModal = (changed = false) => {
    setShow(false);
    setTimeout(() => {
      if (changed) clearSelectedProducts();
      onTaxonomyFilterChange('');
      onNextTaxonomiesBatch(''); // call with filter empty to clear list
    }, 250); // modal timeout before it fully closes
  };

  const config = getConfig({
    onProductRowSelect,
    loading: productLoading,
    selectedProducts
  });

  const canFetchMore = !taxonomyLoading && taxonomies && taxonomyPagination.hasNext;
  const onInfiniteScroll = () => {
    if (canFetchMore) {
      onNextTaxonomiesBatch();
    }
  };

  return (
    <>
      <BrandProductsView
        tableRef={tableRef}
        view={view}
        selectedProducts={selectedProducts}
        onClearSelectedProducts={clearSelectedProducts}
        onClassificationClick={onOpenClassificationModal}
        onViewChange={setView}
        tableConfig={config}
        loading={productLoading}
        products={products}
        total={productsPagination.totalItems}
        displayed={displayed}
        onCardClick={onProductRowSelect}
        onNext={productsPagination.hasNext ? onNextProductsBatch : undefined}
        onPrevious={productsPagination.hasPrevious ? onPreviousProductsBatch : undefined}
      />
      <BrandProductsTaxonomyChangeModal
        loading={taxonomyLoading}
        taxonomies={taxonomies}
        show={show}
        products={selectedProducts}
        onTaxonomyFilterChange={onTaxonomyFilterChange}
        onChangeClassificationClick={upsertProductTaxonomies}
        onClose={onCloseClassificationModal}
        onFetchTaxonomies={canFetchMore ? onInfiniteScroll : undefined}
      />
    </>
  );
};

export default BrandProducts;
